/**
 * Phase 10 R2 — Auto-write helper for labor columns at job insert/edit time.
 *
 * SAFETY CONTRACT (read carefully):
 *   • Hook-level flag gating: if labor_engine_enabled OR ui_ticket_financials
 *     is OFF for the active shop, this helper returns null and the caller
 *     MUST NOT include labor columns in its insert/update payload. Legacy
 *     behavior is preserved exactly when flags are off.
 *   • Sell-side ONLY. Never read by scheduler / cascade / pay / cost engines.
 *   • Never overwrites overrides — callers should only invoke this on insert
 *     or hour-edit; override flow is owned by useUpdateJobLaborOverride.
 *
 * Returns the partial payload to merge into the ticket_jobs row, or null when
 * gates are off.
 */

import { supabase } from '@/integrations/supabase/client';
import {
  computeJobLabor,
  laborColumnsForUpsert,
} from '@/lib/labor/computeJobLabor';

interface AutoWriteArgs {
  shopId: string;
  /** Effective minutes the row will be created/edited with. */
  effectiveMinutes: number;
  /** Job category — fed to resolveLaborRate. */
  jobCategory: string | null | undefined;
  /** Snapshot of the active shop's feature/exposure flags. */
  flags: {
    labor_engine_enabled: boolean;
    ui_ticket_financials: boolean;
  };
}

export async function buildLaborColumnsForAutoWrite(
  args: AutoWriteArgs,
): Promise<ReturnType<typeof laborColumnsForUpsert> | null> {
  if (!args.flags.labor_engine_enabled || !args.flags.ui_ticket_financials) {
    return null;
  }
  // Read shop-level rates fresh to avoid stale cache hazards on first-job insert.
  const { data, error } = await supabase
    .from('shops')
    .select('default_labor_rate, diagnostic_labor_rate, maintenance_labor_rate')
    .eq('id', args.shopId)
    .maybeSingle();
  if (error || !data) return null;

  const computed = computeJobLabor({
    effectiveMinutes: args.effectiveMinutes,
    jobCategory: args.jobCategory ?? null,
    shopRates: {
      default_labor_rate: Number(data.default_labor_rate ?? 0),
      diagnostic_labor_rate:
        data.diagnostic_labor_rate != null
          ? Number(data.diagnostic_labor_rate)
          : null,
      maintenance_labor_rate:
        data.maintenance_labor_rate != null
          ? Number(data.maintenance_labor_rate)
          : null,
    },
    override: null,
  });
  return laborColumnsForUpsert(computed);
}

/**
 * Phase 10 R2 — Refresh labor on an existing ticket_jobs row after its
 * effective minutes changed (e.g. user edited hours, or AddLaborDialog
 * attached-mode added minutes to an existing job).
 *
 * Behavior:
 *   • Flags OFF → no-op (returns null; caller skips labor write).
 *   • Row has no labor columns yet (uncomputed) → no-op. The strict "no
 *     retroactive mutation" rule means we never auto-fill on edit; the user
 *     must press the explicit "Compute labor" affordance.
 *   • Row is overridden → preserves rate (applied=resolved=existing applied);
 *     only `sold_minutes` + `labor_sell_amount` recompute against the override
 *     rate, so the dollar number stays consistent with the new hours.
 *   • Row is computed but not overridden → recomputes against the current
 *     resolver output (rate may shift if shop rate changed between writes; this
 *     is acceptable because the user explicitly touched the row).
 */
export async function refreshLaborForRowEdit(args: {
  ticketId: string;
  jobId: string;
  shopId: string;
  effectiveMinutes: number;
  flags: {
    labor_engine_enabled: boolean;
    ui_ticket_financials: boolean;
  };
}): Promise<void> {
  if (!args.flags.labor_engine_enabled || !args.flags.ui_ticket_financials) return;

  // Read current labor state + category for this row (composite key).
  const { data: row, error: rowErr } = await (supabase as any)
    .from('ticket_jobs')
    .select(
      `labor_sell_amount, labor_rate_overridden, labor_rate_applied,
       jobs:job_id(category)`,
    )
    .eq('ticket_id', args.ticketId)
    .eq('job_id', args.jobId)
    .maybeSingle();
  if (rowErr || !row) return;

  // Strict "no retroactive mutation": uncomputed rows stay uncomputed.
  if (row.labor_sell_amount == null) return;

  const { data: shopData, error: shopErr } = await supabase
    .from('shops')
    .select('default_labor_rate, diagnostic_labor_rate, maintenance_labor_rate')
    .eq('id', args.shopId)
    .maybeSingle();
  if (shopErr || !shopData) return;

  const computed = computeJobLabor({
    effectiveMinutes: args.effectiveMinutes,
    jobCategory: (row.jobs as any)?.category ?? null,
    shopRates: {
      default_labor_rate: Number(shopData.default_labor_rate ?? 0),
      diagnostic_labor_rate:
        shopData.diagnostic_labor_rate != null
          ? Number(shopData.diagnostic_labor_rate)
          : null,
      maintenance_labor_rate:
        shopData.maintenance_labor_rate != null
          ? Number(shopData.maintenance_labor_rate)
          : null,
    },
    // Preserve override if present — rate must NOT auto-revert on hours edit.
    override: row.labor_rate_overridden && row.labor_rate_applied != null
      ? { rate: Number(row.labor_rate_applied) }
      : null,
  });

  await (supabase as any)
    .from('ticket_jobs')
    .update(laborColumnsForUpsert(computed))
    .eq('ticket_id', args.ticketId)
    .eq('job_id', args.jobId);
}
